import cx from 'classnames';
import { forwardRef } from 'react';
import type { VoidFunctionComponent } from 'react';

import { RevisionSource } from '../../../../../typings/enums';
import { compactRevision } from '../../../../helpers/revisions';
import Badge from '../../../generic/Badge/Badge';
import { icons } from '../../../generic/Icon/icons';
import { Icon } from '../../../generic/Icon/index';
import type { Revision } from '../../../../types';

import { getRevisionMessage, getSourceText } from './utils';

import styles from './Dropdown.module.less';

/**
 * Check if a revision should show "Analyzed:" before its commit
 * message. Since CLI revisions always have a timestamp message,
 * we add a "Analyzed:" prefix to clarify the revision value.
 */
const shouldShowAnalyzedLabel = (revision?: Revision) => {
  return revision?.source === RevisionSource.CLI;
};

type BaseProps = {
  shouldShowChevron?: boolean;
  placeholder?: string;
  variant?: 'default' | 'light';
};

type SBOMProps = BaseProps & {
  type: 'sbom';
  selectedRevision: never;
};

type NonSBOMProps = BaseProps & {
  type: string;
  selectedRevision?: Revision;
};

type Props = SBOMProps | NonSBOMProps;

/**
 * Displays a revision in a nice way. This *could* be used for the RevisionDropdown's button,
 * but for some reason react-menu is not playing along nicely and the menu won't open. However,
 * this is still useful for reusability.
 */
const RevisionDisplay: VoidFunctionComponent<Props> = ({
  type,
  selectedRevision,
  shouldShowChevron,
  placeholder,
  variant,
}) => {
  if (!selectedRevision) {
    return (
      <div className={cx(styles.revisionDisplay, {
        [styles.asButton]: shouldShowChevron,
        [styles.light]: variant === 'light',
      })}
      >
        {placeholder ?? 'Select a revision'}
        {shouldShowChevron && <Icon icon={icons.chevronDown} />}
      </div>
    );
  }

  const label = compactRevision(selectedRevision?.locator || '');

  if (type === 'sbom') {
    return (
      <div
        className={cx(styles.revisionDisplay, {
          [styles.asButton]: shouldShowChevron,
          [styles.light]: variant === 'light',
        })}
      >
        <Icon icon={icons.listTree} />
        {label}
        {shouldShowChevron && <Icon icon={icons.chevronDown} />}
      </div>
    );
  }

  const sourceText = getSourceText(selectedRevision?.source);
  const selectedRevisionMessage = getRevisionMessage({
    revision: selectedRevision,
    isSelected: true,
    shouldShowAnalyzedLabel: shouldShowAnalyzedLabel(selectedRevision),
  });

  return (
    <div
      className={cx(styles.revisionDisplay, {
        [styles.asButton]: shouldShowChevron,
        [styles.light]: variant === 'light',
      })}
    >
      {label && <Badge color={variant === 'light' ? 'light-gray' : 'dark-gray'}>{label}</Badge>}
      <span className={styles.revisionMessage}>{selectedRevisionMessage}</span>
      {sourceText && (
        <Badge color={variant === 'light' ? 'light-gray' : 'dark-gray'}>
          <span className={styles.sourceBadgeText}>{sourceText}</span>
        </Badge>
      )}
      {shouldShowChevron && <Icon icon={icons.chevronDown} />}
    </div>
  );
};

/**
 * This needs to be wrapped in a forwardRef so that whatever is using it (usually react-menu) has an attached ref and onClick to work with.
 */
export const RevisionDisplayAsButton: VoidFunctionComponent<Props> = forwardRef<
  HTMLDivElement,
  Props
    >((props, ref) => {
      return (
        <div
          ref={ref}
          // @ts-expect-error There will be an onClick provided by the parent (usually react-menu)
          onClick={props.onClick}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            // @ts-expect-error There will be an onClick provided by the parent (usually react-menu)
            if ((e.key === 'Enter' || e.key === ' ') && props.onClick) {
              // @ts-expect-error There will be an onClick provided by the parent (usually react-menu)
              props.onClick();
            }
          }}
          className={styles.revisionDisplayButtonContainer}
        >
          {/* eslint-disable-next-line react/jsx-props-no-spreading */}
          <RevisionDisplay {...props} shouldShowChevron />
        </div>
      );
    });

export default RevisionDisplay;
